/**
 * Global JavaScript
 */

// Block scripts
import '../../blocks/about/about.js';
import '../../blocks/accordion/accordion.js';
import '../../blocks/testimonials/testimonials.js';

(() => {
  'use strict';

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  document.addEventListener('DOMContentLoaded', () => {
    initMobileMenu();
    initSubmenus();
    initSmoothScroll();
    initHeaderScroll();
  });

  /**
   * Header Scroll Effect
   */
  const initHeaderScroll = () => {
    const header = document.querySelector('.header');
    if (!header) return;

    const onScroll = () => {
      header.classList.toggle('is-scrolled', window.scrollY > 50);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  };

  /**
   * Desktop Submenu Hover + Keyboard
   */
  const initSubmenus = () => {
    const items = document.querySelectorAll('.header__menu-item.has-children');

    items.forEach((item) => {
      let closeTimer;
      const link = item.querySelector('.header__menu-link');
      const submenu = item.querySelector('.header__submenu');

      const openSubmenu = () => {
        clearTimeout(closeTimer);
        // Close other open submenus
        items.forEach((other) => {
          if (other !== item) closeSubmenu(other);
        });
        item.classList.add('is-open');
        if (link) link.setAttribute('aria-expanded', 'true');
      };

      const closeSubmenu = (target = item) => {
        target.classList.remove('is-open');
        const targetLink = target.querySelector('.header__menu-link');
        if (targetLink) targetLink.setAttribute('aria-expanded', 'false');
      };

      // Mouse events
      item.addEventListener('mouseenter', openSubmenu);
      item.addEventListener('mouseleave', () => {
        closeTimer = setTimeout(() => closeSubmenu(), 100);
      });

      // Keyboard: Enter/Space on parent link opens submenu
      if (link) {
        link.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            if (submenu) {
              e.preventDefault();
              const isOpen = item.classList.contains('is-open');
              if (isOpen) {
                closeSubmenu();
              } else {
                openSubmenu();
                const firstLink = submenu.querySelector('a');
                if (firstLink) firstLink.focus();
              }
            }
          }
        });
      }

      // Keyboard: Escape closes submenu and returns focus
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && item.classList.contains('is-open')) {
          closeSubmenu();
          if (link) link.focus();
        }
      });

      // Close when tabbing out of the last submenu item
      if (submenu) {
        const submenuLinks = submenu.querySelectorAll('a');
        const lastLink = submenuLinks[submenuLinks.length - 1];
        if (lastLink) {
          lastLink.addEventListener('keydown', (e) => {
            if (e.key === 'Tab' && !e.shiftKey) {
              closeSubmenu();
            }
          });
        }
      }
    });
  };

  /**
   * Mobile Menu with Focus Trapping
   */
  const initMobileMenu = () => {
    const header = document.querySelector('.header');
    const hamburger = document.querySelector('.header__hamburger');
    const mobileMenu = document.querySelector('.header__mobile-menu');
    const overlay = document.querySelector('.header__mobile-overlay');

    if (!hamburger || !mobileMenu) return;

    let trapHandler = null;
    const menuFocusables = () => mobileMenu.querySelectorAll(FOCUSABLE);

    // Set initial tabindex="-1" on all focusable elements while menu is closed
    menuFocusables().forEach(el => el.setAttribute('tabindex', '-1'));

    const openMenu = () => {
      header.classList.add('menu-open');
      mobileMenu.classList.add('is-open');
      mobileMenu.setAttribute('aria-hidden', 'false');
      overlay.classList.add('is-visible');
      hamburger.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';

      // Restore tabindex on focusable elements
      menuFocusables().forEach(el => el.removeAttribute('tabindex'));

      // Focus first focusable element in menu
      const firstFocusable = mobileMenu.querySelector(FOCUSABLE);
      if (firstFocusable) firstFocusable.focus();

      // Set up focus trap
      trapHandler = (e) => {
        if (e.key !== 'Tab') return;

        const focusableEls = mobileMenu.querySelectorAll(FOCUSABLE);
        if (!focusableEls.length) return;

        const first = focusableEls[0];
        const last = focusableEls[focusableEls.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      };

      mobileMenu.addEventListener('keydown', trapHandler);
    };

    const closeMenu = () => {
      header.classList.remove('menu-open');
      mobileMenu.classList.remove('is-open');
      mobileMenu.setAttribute('aria-hidden', 'true');
      overlay.classList.remove('is-visible');
      hamburger.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';

      // Hide focusable elements from tab order while menu is closed
      menuFocusables().forEach(el => el.setAttribute('tabindex', '-1'));

      // Remove focus trap and return focus
      if (trapHandler) {
        mobileMenu.removeEventListener('keydown', trapHandler);
        trapHandler = null;
      }
      hamburger.focus();
    };

    hamburger.addEventListener('click', () => {
      if (mobileMenu.classList.contains('is-open')) {
        closeMenu();
      } else {
        openMenu();
      }
    });

    overlay.addEventListener('click', closeMenu);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && mobileMenu.classList.contains('is-open')) {
        closeMenu();
      }
    });
  };

  /**
   * Smooth Scroll for Anchor Links
   */
  const initSmoothScroll = () => {
    document.querySelectorAll('a[href^="#"]').forEach((link) => {
      link.addEventListener('click', (e) => {
        const targetId = link.getAttribute('href');
        if (targetId === '#') return;

        const targetElement = document.querySelector(targetId);
        if (targetElement) {
          e.preventDefault();
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  };
})();
